// ============================================================================
// @orgazone/api-envelope/client — LE CLIENT HTTP DES SPA ORGAZONE, source UNIQUE (pendant de `./fastify`).
//
// ⚠️ POURQUOI CE FICHIER EXISTE (panne réelle, 2026-08-01) : le serveur EDI avait basculé sur l'enveloppe
// §16.2.7 pour TOUTES ses routes (y compris `/v1/auth/*`), mais son SPA gardait un second décodeur maison
// qui lisait ces routes À LA RACINE. `mode.armed` valait donc `undefined` → le SPA croyait au « mode ouvert »
// → montait la console → 1ᵉʳ appel admin en 401 → renvoi au login → rechargement → BOUCLE INFINIE : plus
// personne ne pouvait se connecter. Trois SPA avaient recopié le même client et avaient déjà divergé
// (décodage CSRF, refus d'une réponse hors enveloppe, type d'erreur).
//
// RÈGLE : une app n'écrit JAMAIS son propre décodeur d'enveloppe ni son propre `fetch`. Le serveur POSE
// l'enveloppe (`applyEnvelope`), le client la LIT (`createApiClient`) — même package, donc même contrat.
// Ce qui reste propre à chaque app est PARAMÉTRÉ (préfixe, politique 401, traduction des clés), jamais recopié.
// ============================================================================

import { decodeEnvelope, type Humanize } from './decode';

// ⚠️ RÉ-EXPORT et non redéfinition : les SPA importent `ApiError` d'ici depuis toujours, et le type doit
// rester LE MÊME que celui levé par le client de service (sinon `instanceof` diverge d'un côté à l'autre).
export { ApiError } from './decode';
export type { Humanize } from './decode';

export interface ApiClientOptions {
  /** Préfixe appliqué aux chemins relatifs (ex. `/v1`). Un chemin déjà absolu (`/v1/…`) n'est pas re-préfixé. */
  baseUrl?: string;
  /** Nom du cookie CSRF posé par `@orgazone/auth-web` (double-submit). */
  csrfCookie?: string;
  /** Clé machine → texte affichable. À défaut : `meta.detail`, puis la clé elle-même. */
  humanize?: Humanize;
  /**
   * Politique 401 de l'app (session expirée) — ex. renvoyer au login. `path` est le chemin DEMANDÉ.
   * ⚠️ JAMAIS appelé pour les routes `/auth/*` : voir `isAuthRoute`. L'app n'a donc pas à y penser.
   */
  onUnauthorized?: (path: string) => void;
}

/**
 * Vrai pour les routes du socle d'auth (`/auth/me`, `/auth/login`…), quel que soit le préfixe (`/v1/auth/…`).
 * ⚠️ Sur ces routes un 401 est une réponse NORMALE (« pas de session »), PAS une session expirée : c'est
 * précisément la question qu'on pose avant d'être connecté. Y appliquer la politique 401 de l'app crée une
 * boucle — `me()` → 401 → redirection → rechargement → `me()` → 401 → … La console était dans ce cas
 * (`onUnauthorized` inconditionnel), l'EDI l'exemptait à la main. C'est une propriété du CONTRAT d'auth,
 * pas une politique d'application : elle est donc portée ici, pour toutes les apps (SPEC §24.3bis).
 */
export const isAuthRoute = (path: string): boolean => /(^|\/)auth\//.test(path);

/** Lit un cookie non-httpOnly (le jeton CSRF y est déposé en double-submit). */
function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m ? decodeURIComponent(m[1]!) : null;
}

/**
 * Construit le client HTTP d'une SPA : cookies de session, en-tête CSRF sur les mutations, décodage de
 * l'enveloppe §16.2.7, `ApiError` typée. Une réponse HORS enveloppe est REFUSÉE explicitement (§23.0 : on
 * signale une régression serveur au lieu de deviner une forme, ce qui la masquerait).
 */
/**
 * Le nom de fichier annoncé par le serveur (`content-disposition`).
 *
 * ⚠️ `filename*` (RFC 5987) EST LU EN PREMIER : c'est lui qui porte les accents. Ne lire que
 * `filename=` rendrait « relévé.csv » sur un fichier nommé « relevé.csv », ou un nom tronqué.
 * ⚠️ `null` quand l'en-tête manque — à l'appelant de choisir un nom, jamais à nous d'en inventer un.
 */
function nomDeFichier(headers: Headers): string | null {
  const cd = headers.get('content-disposition');
  if (!cd) return null;
  const etoile = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (etoile?.[1]) { try { return decodeURIComponent(etoile[1].trim().replace(/^"|"$/g, '')); } catch { /* nom brut ci-dessous */ } }
  const simple = /filename="?([^";]+)"?/i.exec(cd);
  return simple?.[1]?.trim() ?? null;
}

export function createApiClient(opts: ApiClientOptions = {}) {
  const { baseUrl = '', csrfCookie = 'oz_csrf', humanize, onUnauthorized } = opts;

  async function request<T>(method: string, path: string, body?: unknown, init?: RequestInit): Promise<T> {
    const url = path.startsWith('http') || !baseUrl || path.startsWith(baseUrl) ? path : `${baseUrl}${path}`;
    const isForm = (v: unknown): boolean => typeof FormData !== 'undefined' && v instanceof FormData;
    // Corps EFFECTIF : objet sérialisé ici, ou corps déjà prêt fourni via `init` (chaîne JSON, Blob, ZIP…).
    const payload = body !== undefined ? (isForm(body) ? (body as FormData) : JSON.stringify(body)) : init?.body;

    const headers: Record<string, string> = {};
    // JSON par défaut dès qu'il y a un corps — SAUF FormData (le navigateur doit poser lui-même sa frontière).
    // Un `content-type` explicite dans `init.headers` gagne (fusionné en dernier) : upload binaire, ZIP…
    if (payload != null && !isForm(payload)) headers['content-type'] = 'application/json';
    if (method !== 'GET' && method !== 'HEAD') {
      const t = readCookie(csrfCookie);
      if (t) headers['x-csrf-token'] = t; // sinon les routes protégées répondent 403 csrf_invalid
    }

    const res = await fetch(url, {
      method,
      credentials: 'include', // cookie de session (même origine ; explicite pour robustesse derrière proxy)
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
      body: payload,
    });

    // Politique 401 de l'app — sauf sur /auth/* (cf. `isAuthRoute` : y rebondir boucle). `url` et non `path`
    // pour tester le chemin RÉELLEMENT appelé, que l'appelant l'ait donné relatif ou déjà préfixé.
    if (res.status === 401 && !isAuthRoute(url)) onUnauthorized?.(path);

    // Décodage = PRIMITIVE PARTAGÉE (`decodeEnvelope`), identique au client de service : une seule
    // lecture de la forme §16.2.7 pour toute la plateforme, navigateur comme serveur.
    return decodeEnvelope<T>(res.status, await res.text(), humanize);
  }


  /**
   * TÉLÉCHARGE UN FICHIER — même transport, même sécurité, mais la réponse n'est PAS une enveloppe.
   *
   * ⚠️ POURQUOI DANS LA PRIMITIVE PARTAGÉE ET NON DANS L'APP. Un `fetch` écrit à la main dans un écran
   * repartirait de zéro sur le jeton CSRF, le cookie de session et la politique 401 — trois choses que
   * ce module centralise justement pour qu'elles ne divergent pas. La première copie marche toujours ;
   * c'est la deuxième qui oublie le CSRF, et le symptôme est un 403 que personne ne relie à ça.
   *
   * ⚠️ UNE RÉPONSE FAUTIVE RESTE UNE ENVELOPPE. Le serveur refuse en JSON (motif par motif) et ne
   * rend un flux binaire qu'en cas de succès : on décode donc l'enveloppe sur erreur, pour que
   * l'appelant reçoive le MÊME `ApiError` que partout ailleurs plutôt qu'un fichier de message d'erreur
   * enregistré sur le disque de l'utilisateur — ce qui est le défaut classique des téléchargements.
   */
  async function download(
    method: string, path: string, body?: unknown, init?: RequestInit,
  ): Promise<{ blob: Blob; filename: string | null; headers: Headers }> {
    const url = path.startsWith('http') || !baseUrl || path.startsWith(baseUrl) ? path : `${baseUrl}${path}`;
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (method !== 'GET' && method !== 'HEAD') {
      const t = readCookie(csrfCookie);
      if (t) headers['x-csrf-token'] = t;
    }

    const res = await fetch(url, {
      method,
      credentials: 'include',
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
      body: body !== undefined ? JSON.stringify(body) : init?.body,
    });

    if (res.status === 401 && !isAuthRoute(url)) onUnauthorized?.(path);
    // ⚠️ `decodeEnvelope` LÈVE sur un statut d'échec : c'est ce qu'on veut, et c'est la même erreur
    //    typée que le reste de l'application.
    if (!res.ok) return decodeEnvelope(res.status, await res.text(), humanize) as never;

    return { blob: await res.blob(), filename: nomDeFichier(res.headers), headers: res.headers };
  }

  return {
    request,
    download,
    get: <T>(path: string, init?: RequestInit) => request<T>('GET', path, undefined, init),
    post: <T>(path: string, body?: unknown, init?: RequestInit) => request<T>('POST', path, body, init),
    put: <T>(path: string, body?: unknown, init?: RequestInit) => request<T>('PUT', path, body, init),
    patch: <T>(path: string, body?: unknown, init?: RequestInit) => request<T>('PATCH', path, body, init),
    del: <T>(path: string, init?: RequestInit) => request<T>('DELETE', path, undefined, init),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
